// Sidebar navigasyon verisi + saf yardımcılar — Sidebar.tsx'ten ayrık tutulur
// ki bileşen dosyası yalnız bileşen export etsin (react-refresh) ve NAV/filterNav
// testlerde bağımsız import edilebilsin.
import type { ReactNode } from 'react';
import {
  LayoutDashboard,
  BarChart3,
  FileText,
  Files,
  HelpCircle,
  Tag,
  Globe,
  Users,
  ClipboardList,
  Settings,
  FolderOpen,
  Languages,
  ShieldCheck,
  Megaphone,
  ListChecks,
  ListTree,
  LayoutTemplate,
  FormInput,
  Building2,
  KeyRound,
  Boxes,
  Component,
} from 'lucide-react';
import { PERMISSIONS, type Permission } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';

export interface NavItem {
  key: string;
  /** i18n key in the `nav` namespace, e.g. `items.dashboard`. Resolved at render. */
  labelKey: string;
  icon: ReactNode;
  to: string;
  /**
   * İzin anahtarı. Tanımlıysa öğe yalnızca `useCan(permission)` doğruysa
   * görünür. Parent (children'lı) öğeler ayrıca, en az bir görünür child'ı
   * kalmazsa gizlenir. Route guard'larıyla birebir hizalıdır.
   */
  permission?: Permission;
  children?: NavItem[];
}

export interface NavSection {
  key: string;
  /** i18n key in the `nav` namespace, e.g. `sections.content`. */
  titleKey?: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    key: 'overview',
    items: [
      {
        key: 'dashboard',
        labelKey: 'items.dashboard',
        icon: <LayoutDashboard size={16} strokeWidth={1.8} />,
        to: ROUTES.DASHBOARD,
      },
      {
        key: 'analytics-content',
        labelKey: 'items.analytics',
        icon: <BarChart3 size={16} strokeWidth={1.8} />,
        to: ROUTES.ANALYTICS_CONTENT,
        permission: PERMISSIONS.DASHBOARD_VIEW,
      },
    ],
  },
  {
    key: 'content',
    titleKey: 'sections.content',
    items: [
      {
        key: 'articles',
        labelKey: 'items.articles',
        icon: <FileText size={16} strokeWidth={1.8} />,
        to: ROUTES.ARTICLES,
        permission: PERMISSIONS.ARTICLE_VIEW,
      },
      {
        key: 'pages',
        labelKey: 'items.pages',
        icon: <Files size={16} strokeWidth={1.8} />,
        to: ROUTES.PAGES,
        permission: PERMISSIONS.PAGE_VIEW,
      },
      {
        key: 'sss',
        labelKey: 'items.sss',
        icon: <HelpCircle size={16} strokeWidth={1.8} />,
        to: ROUTES.SSS,
        permission: PERMISSIONS.SSS_VIEW,
      },
      {
        key: 'campaigns',
        labelKey: 'items.campaigns',
        icon: <Megaphone size={16} strokeWidth={1.8} />,
        to: ROUTES.CAMPAIGNS,
        permission: PERMISSIONS.CAMPAIGN_VIEW,
      },
      {
        key: 'workflow-queue',
        labelKey: 'items.workflowQueue',
        icon: <ListChecks size={16} strokeWidth={1.8} />,
        to: ROUTES.WORKFLOW_QUEUE,
        permission: PERMISSIONS.WORKFLOW_QUEUE_VIEW,
      },
      {
        key: 'menus',
        labelKey: 'items.menus',
        icon: <ListTree size={16} strokeWidth={1.8} />,
        to: ROUTES.MENUS,
        permission: PERMISSIONS.MENU_MANAGE,
      },
      {
        key: 'forms',
        labelKey: 'items.forms',
        icon: <FormInput size={16} strokeWidth={1.8} />,
        to: ROUTES.FORMS,
        permission: PERMISSIONS.FORM_VIEW,
      },
      {
        key: 'branches',
        labelKey: 'items.branches',
        icon: <Building2 size={16} strokeWidth={1.8} />,
        to: ROUTES.BRANCHES,
        permission: PERMISSIONS.BRANCH_VIEW,
      },
      {
        key: 'components',
        labelKey: 'items.components',
        icon: <Component size={16} strokeWidth={1.8} />,
        to: ROUTES.COMPONENTS,
        permission: PERMISSIONS.COMPONENT_MANAGE,
      },
      {
        key: 'widgets',
        labelKey: 'items.widgets',
        icon: <LayoutTemplate size={16} strokeWidth={1.8} />,
        to: ROUTES.SLIDERS,
        permission: PERMISSIONS.SLIDER_MANAGE,
        children: [
          { key: 'sliders', labelKey: 'items.sliders', icon: null, to: ROUTES.SLIDERS, permission: PERMISSIONS.SLIDER_MANAGE },
          { key: 'banners', labelKey: 'items.banners', icon: null, to: ROUTES.BANNERS, permission: PERMISSIONS.BANNER_MANAGE },
          { key: 'popups', labelKey: 'items.popups', icon: null, to: ROUTES.POPUPS, permission: PERMISSIONS.POPUP_MANAGE },
        ],
      },
      {
        key: 'taxonomy',
        labelKey: 'items.taxonomy',
        icon: <Tag size={16} strokeWidth={1.8} />,
        to: ROUTES.CATEGORIES,
        permission: PERMISSIONS.CATEGORY_MANAGE,
        children: [
          { key: 'categories', labelKey: 'items.categories', icon: null, to: ROUTES.CATEGORIES, permission: PERMISSIONS.CATEGORY_MANAGE },
          { key: 'tags', labelKey: 'items.tags', icon: null, to: ROUTES.TAGS, permission: PERMISSIONS.TAG_MANAGE },
          { key: 'authors', labelKey: 'items.authors', icon: null, to: ROUTES.AUTHORS, permission: PERMISSIONS.AUTHOR_MANAGE },
        ],
      },
      {
        key: 'media',
        labelKey: 'items.media',
        icon: <FolderOpen size={16} strokeWidth={1.8} />,
        to: ROUTES.MEDIA,
        permission: PERMISSIONS.FILES_READ,
      },
    ],
  },
  {
    key: 'system',
    titleKey: 'sections.system',
    items: [
      {
        key: 'users',
        labelKey: 'items.users',
        icon: <Users size={16} strokeWidth={1.8} />,
        to: ROUTES.ADMIN_USERS,
        permission: PERMISSIONS.ADMIN_USER_MANAGE,
        children: [
          { key: 'admin-users', labelKey: 'items.admin-users', icon: null, to: ROUTES.ADMIN_USERS, permission: PERMISSIONS.ADMIN_USER_MANAGE },
          { key: 'roles', labelKey: 'items.roles', icon: null, to: ROUTES.ROLES, permission: PERMISSIONS.ROLE_MANAGE },
          { key: 'kvkk', labelKey: 'items.kvkk', icon: null, to: ROUTES.KVKK_DELETIONS, permission: PERMISSIONS.KVKK_DELETION_MANAGE },
        ],
      },
      {
        key: 'audit',
        labelKey: 'items.audit',
        icon: <ClipboardList size={16} strokeWidth={1.8} />,
        to: ROUTES.AUDIT_LOG,
        permission: PERMISSIONS.AUDIT_LOG_VIEW,
      },
      {
        key: 'ip-allowlist',
        labelKey: 'items.ip-allowlist',
        icon: <ShieldCheck size={16} strokeWidth={1.8} />,
        to: ROUTES.IP_ALLOWLIST,
        permission: PERMISSIONS.SECURITY_MANAGE,
      },
      {
        key: 'api-keys',
        labelKey: 'items.api-keys',
        icon: <KeyRound size={16} strokeWidth={1.8} />,
        to: ROUTES.API_KEYS,
        permission: PERMISSIONS.SECURITY_MANAGE,
      },
      {
        key: 'web',
        labelKey: 'items.web',
        icon: <Globe size={16} strokeWidth={1.8} />,
        to: ROUTES.WEB_REDIRECTS,
        permission: PERMISSIONS.WEB_REDIRECT,
        children: [
          { key: 'web-redirects', labelKey: 'items.web-redirects', icon: null, to: ROUTES.WEB_REDIRECTS, permission: PERMISSIONS.WEB_REDIRECT },
          { key: 'web-og', labelKey: 'items.web-og', icon: null, to: ROUTES.WEB_OG_PREVIEW, permission: PERMISSIONS.WEB_OG_PREVIEW },
          { key: 'web-seo', labelKey: 'items.web-seo', icon: null, to: ROUTES.WEB_SEO, permission: PERMISSIONS.SETTINGS_MANAGE },
        ],
      },
      {
        key: 'localization',
        labelKey: 'items.localization',
        icon: <Languages size={16} strokeWidth={1.8} />,
        to: ROUTES.TRANSLATIONS,
        permission: PERMISSIONS.TRANSLATION_MANAGE,
        children: [
          { key: 'languages', labelKey: 'items.languages', icon: null, to: ROUTES.LANGUAGES, permission: PERMISSIONS.SETTINGS_MANAGE },
          { key: 'translations', labelKey: 'items.translations', icon: null, to: ROUTES.TRANSLATIONS, permission: PERMISSIONS.TRANSLATION_MANAGE },
        ],
      },
      {
        key: 'projects',
        labelKey: 'items.projects',
        icon: <Boxes size={16} strokeWidth={1.8} />,
        to: ROUTES.PROJECTS,
        permission: PERMISSIONS.PROJECT_MANAGE,
      },
      {
        key: 'settings',
        labelKey: 'items.settings',
        icon: <Settings size={16} strokeWidth={1.8} />,
        to: ROUTES.SETTINGS,
        permission: PERMISSIONS.SETTINGS_MANAGE,
        children: [
          { key: 'settings-general', labelKey: 'items.settings-general', icon: null, to: ROUTES.SETTINGS, permission: PERMISSIONS.SETTINGS_MANAGE },
          { key: 'smtp', labelKey: 'items.smtp', icon: null, to: ROUTES.SMTP_SETTINGS, permission: PERMISSIONS.SETTINGS_MANAGE },
          { key: 'ldap', labelKey: 'items.ldap', icon: null, to: ROUTES.LDAP_SETTINGS, permission: PERMISSIONS.SECURITY_MANAGE },
        ],
      },
    ],
  },
];

const ALL_ITEMS: NavItem[] = NAV.flatMap((s) => s.items);

export function findItemByKey(key: string, items: NavItem[] = ALL_ITEMS): NavItem | undefined {
  for (const it of items) {
    if (it.key === key) return it;
    if (it.children) {
      const found = findItemByKey(key, it.children);
      if (found) return found;
    }
  }
  return undefined;
}

// Segment-boundary match: `/police` should NOT match `/policies/access-log`.
function isRouteMatch(pathname: string, to: string): boolean {
  if (!to) return false;
  if (pathname === to) return true;
  const prefix = to.endsWith('/') ? to : `${to}/`;
  return pathname.startsWith(prefix);
}

// Pick the most specific (longest `to`) match across all nav items + children.
export function findSelectedKeys(pathname: string): string[] {
  let bestKey = '';
  let bestLen = -1;
  const consider = (key: string, to: string) => {
    if (isRouteMatch(pathname, to) && to.length > bestLen) {
      bestKey = key;
      bestLen = to.length;
    }
  };
  // Visit children FIRST so a child wins on equal-length tie with its parent
  // (e.g. parent.to and first-child.to both equal `/vehicles` — the child
  // "Araçlar" should be the highlighted leaf, not the parent "Araç & Marka").
  for (const item of ALL_ITEMS) {
    for (const c of item.children ?? []) consider(c.key, c.to);
  }
  for (const item of ALL_ITEMS) {
    consider(item.key, item.to);
  }
  return bestKey ? [bestKey] : [];
}

export function findOpenKeys(pathname: string): string[] {
  // Find the parent whose most-specific child matches.
  let bestParent = '';
  let bestLen = -1;
  for (const item of ALL_ITEMS) {
    if (!item.children?.length) continue;
    for (const c of item.children) {
      if (isRouteMatch(pathname, c.to) && c.to.length > bestLen) {
        bestParent = item.key;
        bestLen = c.to.length;
      }
    }
  }
  return bestParent ? [bestParent] : [];
}

type Can = (permission: Permission) => boolean;

/**
 * NAV'ı kullanıcının izinlerine göre süzer: izin tanımlı ve reddedilmiş
 * leaf'ler düşer; parent ayrıca görünür child'ı kalmazsa düşer; tüm öğeleri
 * gizlenen section da dışarıda kalır. Route guard'larıyla birebir.
 */
export function filterNav(sections: NavSection[], can: Can): NavSection[] {
  const filterItems = (items: NavItem[]): NavItem[] =>
    items.reduce<NavItem[]>((acc, item) => {
      if (item.children?.length) {
        // Parent (genişletilebilir submenu): görünür child'ı kalmazsa düşer.
        // Kendi `permission`'ı navigasyon hedefi içindir; görünürlüğü
        // child'ları belirler (en az biri izinliyse grup gösterilir).
        const children = filterItems(item.children);
        if (children.length === 0) return acc;
        acc.push({ ...item, children });
      } else {
        if (item.permission && !can(item.permission)) return acc;
        acc.push(item);
      }
      return acc;
    }, []);

  return sections
    .map((s) => ({ ...s, items: filterItems(s.items) }))
    .filter((s) => s.items.length > 0);
}
